import axios from "axios"
import { flatten } from "lodash"
import { asyncThunkOf } from "../../common/utils/redux-helpers"
import { Preconditions, ReqStatus } from "../../common/utils/utils"
import { DislikedItem, DislikedItemType, IDisplayItem, IMultiSelectDisplayItem, isMultiSelectDisplayItem } from "./feedback-form-model"

export interface ExplicitFeedbackRequest {
    encryptedInfo?: string
    feedbackText?: string
    jobKey?: string
}

export const submitExplicitFeedback = asyncThunkOf<ReqStatus, { explicitFeedbackRequest: ExplicitFeedbackRequest }>(
    "submitExplicitFeedback",
    async (payload, thunkApi) => {
        const {
            explicitFeedbackRequest: { encryptedInfo, feedbackText, jobKey },
        } = payload
        const apiUrl = thunkApi.getState().config.apiBase
        const { opts, requirements } = thunkApi.getState().feedbackForm
        const displayedPreferences = filterPreferencesToDisplayed(opts)
        const explicitFeedback = buildFeedbackLog(opts, feedbackText)
        try {
            // If a jobSeeker gets here without encryptedInfo or a jobKey we need to know how
            Preconditions.checkState(encryptedInfo !== undefined || jobKey !== undefined)
        } catch {
            return ReqStatus.FAILED
        }

        return axios
            .post<boolean>(`${apiUrl}/feedback/explicit`, {
                info: encryptedInfo,
                otherText: feedbackText,
                displayedPreferences,
                explicitFeedback,
                jobKey,
                negativeLicenses: filterAndConvertJobRequirements(requirements.licenses),
                negativeCertifications: filterAndConvertJobRequirements(requirements.certifications),
                negativeEducations: filterAndConvertJobRequirements(requirements.educations),
                negativeLanguages: filterAndConvertJobRequirements(requirements.languages),
                negativeSkills: filterAndConvertJobRequirements(requirements.skills),
            })
            .then(() => {
                return ReqStatus.SUCCESS
            })
            .catch((error) => {
                if (error.response && error.response.status === 422) {
                    return ReqStatus.LIMIT_REACHED
                }
                return (error.response && error.response.status) !== 503 ? ReqStatus.FAILED : ReqStatus.SUCCESS
            })
    }
)

export const filterAndConvertJobRequirements = (requirements: IDisplayItem[]) =>
    requirements.filter((it) => it.selected).map((it) => ({ suid: it.value, label: it.unselectedText }))

export const logDisplayedPreferencesBeforeSubmit = asyncThunkOf<ReqStatus, { info?: string }>("logCount", async (payload, thunkApi) => {
    const { opts } = thunkApi.getState().feedbackForm
    const { apiBase } = thunkApi.getState().config
    const { info } = payload
    try {
        await axios.post(`${apiBase}/feedback/log-displayed-preferences-data?info=${info}`, filterPreferencesToDisplayed(opts))
        return ReqStatus.SUCCESS
    } catch (err) {
        console.error("failed to send displayed preferences data", err)
        return ReqStatus.FAILED
    }
})

export const filterPreferencesToDisplayed = (
    opts: (IDisplayItem | IMultiSelectDisplayItem)[]
): (DislikedItemType | "multiselect-location")[] => {
    return flatten(
        opts
            .filter((it) => it.isDisplayed)
            .map((it) => {
                if (isMultiSelectDisplayItem(it) && it.field === "multiselect-location") {
                    return it.additionalSelections.map((s) => s.key as DislikedItemType)
                }
                return it.field
            })
    )
}

export const buildFeedbackLog = (opts: (IDisplayItem | IMultiSelectDisplayItem)[], otherText?: string): DislikedItem[] => {
    const explicitFeedback = reduceDisplayItemsToDislikedItems(opts)
    if (otherText) {
        explicitFeedback.push({ type: "OTHER", payload: otherText })
    }
    return explicitFeedback
}

const reduceDisplayItemsToDislikedItems = (opts: (IDisplayItem | IMultiSelectDisplayItem)[]): DislikedItem[] => {
    return opts
        .filter((it) => it.selected)
        .reduce((acc, it) => {
            switch (it.field) {
                case "DISLIKED_COMPANY_SECTOR":
                case "DISLIKED_OCCUPATION_CATEGORY":
                case "DISLIKED_JOBTYPE":
                case "DISLIKED_IQ_REQUIREMENT":
                    return [...acc, { type: it.field, payload: it.value }]
                case "DISLIKED_NORMTITLE":
                case "DISLIKED_EXTRACTED_PAY":
                case "DISLIKED_COMPANY_NAME":
                case "DISLIKED_CITY":
                case "DISLIKED_STATE":
                case "DOESNT_WANT_REMOTE":
                case "DISLIKED_POSTAL_CODE":
                    return [...acc, { type: it.field }]
                case "multiselect-location": {
                    const d = it as IMultiSelectDisplayItem
                    const dislikes = d.additionalSelections.filter((s) => s.selected).map((x) => ({ type: x.key })) as {
                        type: DislikedItemType
                    }[]
                    // NOTE: if jobseeker doesn't select any sub-items, log state as a dislike if city doesn't exist, otherwise log city
                    if (dislikes.length === 0) {
                        const cityOptionDisplayed = d.additionalSelections.some((pref) => pref.key === "DISLIKED_CITY")
                        const stateOptionDisplayed = d.additionalSelections.some((pref) => pref.key === "DISLIKED_STATE")
                        if (stateOptionDisplayed && !cityOptionDisplayed) {
                            dislikes.push({ type: "DISLIKED_STATE" })
                        } else {
                            dislikes.push({ type: "DISLIKED_CITY" })
                        }
                    }
                    return [...acc, ...dislikes]
                }
                // NOTE: OTHER text is set when submitting form
                case "OTHER":
                case "NOT_LOOKING":
                    return acc
                default:
                    return []
            }
        }, [] as DislikedItem[])
}
